/**
 * OSI-818: чистые функции ленты PdfViewer — раскладка страниц, окно отрисовки, текущая
 * страница и поиск страницы по тексту. Вынесены из компонента, чтобы их можно было
 * проверить юнит-тестами без pdf.js и без DOM.
 */

/** Пропорция A4 (высота / ширина) — запасная, пока pdf.js не отдал размеры страниц. */
export const A4_RATIO = 297 / 210;

export interface PagesLayout {
  /** Смещение верхнего края каждой страницы от начала ленты, px. */
  offsets: number[];
  /** Высота каждой страницы при заданной ширине, px. */
  heights: number[];
  /** Полная высота ленты, px. */
  totalHeight: number;
}

/**
 * Раскладка ленты: страница i занимает `width * ratio[i]` по высоте, между страницами `gap`,
 * сверху и снизу ленты — `padding`. Высоты известны ДО отрисовки, поэтому на месте ещё
 * не отрисованных страниц стоят заглушки ровно той же высоты — прокрутка не прыгает.
 */
export const computePagesLayout = (ratios: number[], width: number, gap: number, padding: number): PagesLayout => {
  const offsets: number[] = [];
  const heights: number[] = [];
  let top = padding;

  ratios.forEach((ratio, index) => {
    const height = Math.round(width * (ratio > 0 ? ratio : A4_RATIO));
    offsets.push(top);
    heights.push(height);
    top += height + (index < ratios.length - 1 ? gap : 0);
  });

  return { offsets, heights, totalHeight: top + padding };
};

/**
 * Окно отрисовки: индексы страниц (включительно), пересекающих видимую область,
 * расширенную на `overscan` px вверх и вниз. Всё, что вне окна, — заглушки.
 * Пустой документ — [0, -1].
 */
export const getRenderRange = (
  layout: PagesLayout,
  scrollTop: number,
  viewportHeight: number,
  overscan: number
): [number, number] => {
  const { offsets, heights } = layout;
  const from = scrollTop - overscan;
  const to = scrollTop + viewportHeight + overscan;
  let first = -1;
  let last = -1;

  for (let i = 0; i < offsets.length; i += 1) {
    const top = offsets[i];
    const bottom = top + heights[i];
    if (bottom >= from && top <= to) {
      if (first === -1) first = i;
      last = i;
    }
  }

  return first === -1 ? [0, -1] : [first, last];
};

/**
 * Текущая страница (0-based) для «Страница N из M»: последняя страница, верх которой
 * поднялся выше линии на трети высоты области просмотра. Так номер меняется, когда
 * следующая страница заняла заметную часть экрана, а не на первом её пикселе.
 */
export const getCurrentPageIndex = (layout: PagesLayout, scrollTop: number, viewportHeight: number): number => {
  const line = scrollTop + viewportHeight / 3;
  let current = 0;

  for (let i = 0; i < layout.offsets.length; i += 1) {
    if (layout.offsets[i] <= line) current = i;
    else break;
  }

  return current;
};

/**
 * Нормализация для поиска: pdf.js отдаёт текст страницы кусками (items), и номер акта
 * может прийти разбитым на несколько items или с пробелами между ними. Убираем ВСЕ
 * пробельные символы (\s в JS включает и неразрывный пробел U+00A0) и приводим к нижнему регистру — номер
 * находится независимо от того, как pdf.js нарезал строку.
 */
export const normalizeSearchText = (value: string): string => value.replace(/\s+/g, '').toLowerCase();

/**
 * Индекс (0-based) первой страницы, текст которой содержит `needle`; -1 — не найдено
 * или искать нечего. Тексты страниц — уже склеенные строки.
 */
export const findPageIndexByText = (pageTexts: string[], needle: string | null | undefined): number => {
  const normalizedNeedle = normalizeSearchText(needle ?? '');

  if (!normalizedNeedle) return -1;

  return pageTexts.findIndex((text) => normalizeSearchText(text).includes(normalizedNeedle));
};

/** Минимально нужная часть PDFDocumentProxy из pdf.js — структурный тип вместо прямой зависимости от pdfjs-dist. */
export interface PdfTextSource {
  numPages: number;
  getPage(pageNumber: number): Promise<{
    getTextContent(): Promise<{ items: Array<{ str?: string } | object> }>;
  }>;
}

/**
 * Поиск страницы с текстом прямо в документе pdf.js: страницы НЕ рендерятся,
 * `getTextContent` дешёвый. Идём по порядку и останавливаемся на первом совпадении.
 * `isCancelled` проверяется между страницами — диалог закрыли, поиск прекращаем.
 * Возвращает 0-based индекс или -1.
 */
export const findPageIndexInDocument = async (
  pdf: PdfTextSource,
  needle: string | null | undefined,
  isCancelled: () => boolean = () => false
): Promise<number> => {
  if (!normalizeSearchText(needle ?? '')) return -1;

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    if (isCancelled()) return -1;

    // eslint-disable-next-line no-await-in-loop -- последовательно: останавливаемся на первом совпадении
    const page = await pdf.getPage(pageNumber);
    // eslint-disable-next-line no-await-in-loop
    const content = await page.getTextContent();
    const text = content.items.map((item) => ('str' in item && typeof item.str === 'string' ? item.str : '')).join('');

    if (findPageIndexByText([text], needle) === 0) return pageNumber - 1;
  }

  return -1;
};
